import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import HeroSection from "@/components/home/HeroSection";
import QuickAnswerSection from "@/components/home/QuickAnswerSection";
import ComparisonTable from "@/components/ComparisonTable";
import ProductCard from "@/components/ProductCard";
import EducationalSection from "@/components/home/EducationalSection";
import BuyingGuideSection from "@/components/home/BuyingGuideSection";
import UseCaseSection from "@/components/home/UseCaseSection";
import FAQSection from "@/components/home/FAQSection";
import EmailCapture from "@/components/EmailCapture";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import SEOHead from "@/components/SEOHead";
import { ArrowRight } from "lucide-react";
import ArticleCard from "@/components/ArticleCard";
import ClusterHub from "@/components/home/ClusterHub";

const SolarCalculator = lazy(() => import("@/components/SolarCalculator"));

const POPULAR_GUIDES = [
  {
    slug: "most-efficient-solar-panels-commercially-available",
    title: "Most Efficient Solar Panels Commercially Available",
    metaDescription: "Compare what efficiency means at the panel level and what buyers should check before choosing a high-efficiency module.",
    category: "buyer-guide",
    tags: ["high efficiency", "solar panels"],
  },
  {
    slug: "best-portable-solar-panels-for-camping",
    title: "Best Portable Solar Panels for Camping",
    metaDescription: "Learn how to compare portable solar panels for camping by wattage, portability, connectors, and power-station compatibility.",
    category: "buyer-guide",
    tags: ["portable solar", "camping"],
  },
  {
    slug: "best-flexible-solar-panels-for-rv",
    title: "Best Flexible Solar Panels for RV Roofs",
    metaDescription: "A practical guide to flexible solar panels for RV roofs, including weight, mounting, heat, curvature, and electrical compatibility.",
    category: "buyer-guide",
    tags: ["flexible solar", "RV"],
  },
  {
    slug: "are-bifacial-solar-panels-worth-it",
    title: "Are Bifacial Solar Panels Worth It?",
    metaDescription: "See when rear-side production can help, what limits bifacial gains, and what buyers should inspect before choosing a bifacial panel.",
    category: "educational",
    tags: ["bifacial", "solar education"],
  },
  {
    slug: "ecoflow-solar-panel-compatibility-guide",
    title: "EcoFlow Solar Panel Compatibility Guide",
    metaDescription: "Learn how to compare panel voltage, current, wattage, connectors, and model-specific PV limits before connecting solar to EcoFlow.",
    category: "buying-guide",
    tags: ["EcoFlow", "compatibility"],
  },
  {
    slug: "van-life-solar-panel-buying-guide",
    title: "Van Life Solar Panel Buying Guide",
    metaDescription: "Plan a van-life solar setup around roof space, daily energy use, mounting style, charging hardware, and battery compatibility.",
    category: "buying-guide",
    tags: ["van life", "solar sizing"],
  },
];

const HOME_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Highest Efficiency Solar Cell Guide 2026",
  "description": "Find the highest efficiency solar cell technologies and best high-efficiency solar panels for RVs, camping, solar generators, and off-grid power.",
  "url": "https://highestefficiencysolarcell.com",
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://highestefficiencysolarcell.com" }]
  }
};

const BUYER_PAGES = [
  { label: "Best High-Efficiency Solar Panels", path: "/best-high-efficiency-solar-panels", desc: "Top-rated panels by efficiency across all categories" },
  { label: "Highest Efficiency Solar Panels", path: "/highest-efficiency-solar-panels", desc: "What makes a panel truly high-efficiency?" },
  { label: "Best Portable Solar Panels", path: "/best-portable-solar-panels", desc: "Foldable and portable panels for camping & travel" },
  { label: "Best Solar Panels for RV", path: "/best-solar-panels-for-rv", desc: "Roof-mounted panels sized for RV boondocking" },
  { label: "Best Panels for Solar Generators", path: "/best-solar-panels-for-solar-generators", desc: "Compatible panels for EcoFlow, Jackery, Bluetti" },
  { label: "Best Bifacial Solar Panels", path: "/bifacial-solar-panels", desc: "Dual-sided panels for maximum energy harvest" },
  { label: "Best Flexible Solar Panels", path: "/flexible-solar-panels", desc: "Lightweight panels for curved surfaces & boats" },
  { label: "Solar Panel Calculator", path: "/solar-panel-calculator", desc: "Calculate exactly how many watts you need" },
  { label: "Solar Cell vs Solar Panel", path: "/solar-cell-vs-solar-panel", desc: "What's the difference and why it matters" },
  { label: "Mono vs Polycrystalline", path: "/monocrystalline-vs-polycrystalline-solar-panels", desc: "Which panel type should you choose?" },
];

export default function Home() {
  const [productsReady, setProductsReady] = useState(false);

  useEffect(() => {
    let timer;
    let idleId;

    const enableProducts = () => setProductsReady(true);

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(enableProducts, { timeout: 2200 });
    } else {
      timer = window.setTimeout(enableProducts, 1200);
    }

    return () => {
      if (idleId && "cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  const { data: products = [] } = useQuery({
    queryKey: ["solar-products"],
    queryFn: async () => {
      const { base44 } = await import("@/api/base44Client");
      return base44.entities.SolarProduct.list("sort_order", 50);
    },
    enabled: productsReady,
  });

  const featuredProducts = products.filter((p) => p.featured);
  const tableProducts = products.slice(0, 10);

  return (
    <>
      <SEOHead
        title="Highest Efficiency Solar Cell Guide 2026 | Best High-Efficiency Solar Panels"
        description="Find the highest efficiency solar cell technologies and best high-efficiency solar panels for RVs, camping, solar generators, and off-grid power. Updated 2026 buyer's guide."
        canonical="https://highestefficiencysolarcell.com"
        schema={HOME_SCHEMA}
      />

      <div className="pb-20 lg:pb-0">
        <HeroSection />
        <QuickAnswerSection />

        {/* Comparison Table — above the fold content */}
        {tableProducts.length > 0 && (
          <section className="py-16 lg:py-24 bg-secondary/30">
            <div className="max-w-7xl mx-auto px-4">
              <AffiliateDisclosure />
              <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-3">
                Top High-Efficiency Solar Panels Compared
              </h2>
              <p className="text-muted-foreground mb-8">
                Quick comparison of the best high-efficiency solar panels by category — wattage, efficiency, type, and best use.
              </p>
              <ComparisonTable products={tableProducts} />
              <div className="mt-6 text-center">
                <Link to="/best-high-efficiency-solar-panels" className="inline-flex items-center gap-2 text-sm font-medium text-accent-text hover:underline">
                  See full buyer's guide with pros, cons & reviews <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Featured Product Cards */}
        {featuredProducts.length > 0 && (
          <section className="py-16 lg:py-24">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-3">
                Top Picks: Highest Efficiency Solar Panels
              </h2>
              <p className="text-muted-foreground mb-8">
                Our recommended high-efficiency solar panels across every category and use case. All links go to Amazon — we earn a small commission at no extra cost to you.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          </section>
        )}

        <EducationalSection />

        <ClusterHub />

        <BuyingGuideSection />
        <Suspense fallback={<div className="py-24" />}>
          <SolarCalculator />
        </Suspense>
        <UseCaseSection />

        {/* Solar Guides Hub */}
        <section className="py-16 lg:py-24 bg-secondary/30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-2">
                  Popular Solar Guides
                </h2>
                <p className="text-muted-foreground">
                  Buyer's guides, how-to articles, and sizing guides for every solar use case.
                </p>
              </div>
              <Link to="/solar-guides" className="inline-flex items-center gap-2 text-sm font-mono text-accent-text hover:underline shrink-0">
                View all solar guides <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {POPULAR_GUIDES.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>
        <FAQSection />
        <EmailCapture source="homepage" />

        {/* Long-form SEO intro */}
        <section className="py-16 lg:py-24">
          <div className="max-w-3xl mx-auto px-4 prose-like">
            <h2 className="font-heading font-bold text-2xl mb-4">
              About This Highest Efficiency Solar Cell Guide
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              The term <strong>highest efficiency solar cell</strong> refers to the photovoltaic cells that convert the greatest percentage of sunlight into usable electricity. Lab-record efficiency cells (over 47% using multi-junction technology) are fascinating but not what most buyers need. What matters for real-world purchases is <em>panel-level efficiency</em> — typically 20–24% for today's best monocrystalline, PERC, HJT, TOPCon, and IBC panels.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              This site is an independent buyer's guide focused on helping you understand <strong>solar panel efficiency</strong>, choose the right panel type, and find the best high-efficiency solar panels for your specific situation — whether that's an RV roof, a portable setup for camping, a solar generator system, or a permanent off-grid installation.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              We compare the <strong>most efficient solar panels</strong> available on Amazon, with clear specs, honest pros and cons, and transparent affiliate disclosures. We never fabricate test results, fake star ratings, or display prices we can't keep updated — every product links directly to Amazon where you can see the current price.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}